(() => {
  const KEY = 'sv-light';
  const remember = {
    get() { try { return sessionStorage.getItem(KEY); } catch (e) { return null; } },
    set(v) { try { sessionStorage.setItem(KEY, v); } catch (e) {} }
  };

  // Unlit images carry their srcset in data-srcset until they are needed.
  function loadDeferred(root) {
    root.querySelectorAll('img[data-sv-defer]').forEach((img) => {
      img.srcset = img.dataset.srcset;
      img.removeAttribute('data-sv-defer');
    });
  }
  const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1200));
  window.addEventListener('load', () => idle(() => loadDeferred(document), { timeout: 4000 }));
  document.addEventListener('pointerdown', (e) => {
    const sw = e.target.closest('[data-sv-switch]');
    if (sw) loadDeferred(sw.closest('[data-light-scope]') || document);
  }, { passive: true });

  // One switch per scope; the scope's data-light drives every image inside it.
  function setLight(scope, state, persist) {
    if (state === 'off') loadDeferred(scope);
    if (state === 'on' && scope.dataset.light === 'off') { // warm-up of the lit photos, like a dimmer
      scope.classList.remove('is-igniting'); void scope.offsetWidth;
      scope.classList.add('is-igniting');
      clearTimeout(scope.svIgnite);
      scope.svIgnite = setTimeout(() => scope.classList.remove('is-igniting'), 1500);
    }
    scope.dataset.light = state;
    scope.querySelectorAll('[data-sv-switch]').forEach((sw) => {
      sw.setAttribute('aria-checked', String(state === 'on'));
      const label = sw.querySelector('[data-sv-switch-label]');
      if (label) label.textContent = state === 'on' ? 'Lights on' : 'Lights off';
    });
    scope.dispatchEvent(new CustomEvent('sv:light', { bubbles: true, detail: { state } }));
    if (persist) remember.set(state);
  }

  function initScopes(root) {
    const saved = remember.get();
    root.querySelectorAll('[data-light-scope]').forEach((scope) => {
      if (saved === 'on' || saved === 'off') setLight(scope, saved, false);
    });
  }

  document.addEventListener('click', (e) => {
    const sw = e.target.closest('[data-sv-switch]');
    if (!sw) return;
    const scope = sw.closest('[data-light-scope]');
    if (scope) setLight(scope, scope.dataset.light === 'on' ? 'off' : 'on', true);
  });

  // Hero: start in daylight, then let the evening and the lights come in.
  function initHero(root) {
    const hero = root.querySelector('[data-sv-hero].is-day');
    if (!hero) return;
    const imgs = [...hero.querySelectorAll('img')];
    const loaded = imgs.map((img) => img.complete ? Promise.resolve() : new Promise((r) => {
      img.addEventListener('load', r, { once: true });
      img.addEventListener('error', r, { once: true });
    }));
    const ignite = () => hero.classList.remove('is-day');
    Promise.all(loaded).then(() => setTimeout(ignite, 900));
    setTimeout(ignite, 5000);
  }

  // Product page: variant selection, gallery by size, deposit amounts, cart drawer.
  function initProduct(root) {
    const pdp = root.querySelector('[data-sv-product]');
    if (!pdp) return;
    const variants = JSON.parse(pdp.querySelector('[data-sv-variants]').textContent);
    const form = pdp.querySelector('form[data-sv-form]');
    const idInput = form.querySelector('input[name="id"]');
    const noteInput = form.querySelector('[data-sv-note]');
    const errorBox = pdp.querySelector('[data-sv-error]');
    const buttons = [...document.querySelectorAll('[data-sv-buy]')];
    const setText = (sel, text) => document.querySelectorAll(sel).forEach((el) => { el.textContent = text; });

    const selected = () => [...form.querySelectorAll('[data-sv-option]')].map((fs) => {
      const checked = fs.querySelector('input:checked');
      return checked ? checked.value : null;
    });

    function update() {
      const opts = selected();
      const v = variants.find((x) => x.options.every((o, i) => o === opts[i]));
      form.querySelectorAll('[data-sv-option]').forEach((fs, i) => {
        const out = fs.querySelector('[data-sv-value]');
        if (out) out.textContent = opts[i] || '';
      });
      if (!v) {
        buttons.forEach((b) => { b.disabled = true; });
        setText('[data-sv-buy-label]', 'Unavailable');
        return;
      }
      idInput.value = v.id;
      noteInput.value = v.note;
      setText('[data-sv-total]', v.total);
      setText('[data-sv-deposit]', v.deposit);
      setText('[data-sv-balance]', v.balance);
      setText('[data-sv-buy-label]', v.available ? 'Pre-order with a ' + v.deposit + ' deposit' : 'Unavailable');
      buttons.forEach((b) => { b.disabled = !v.available; });
      const status = pdp.querySelector('[data-sv-status]');
      if (status) status.textContent = opts.join(', ') + ': ' + v.total + ', ' + v.deposit + ' deposit today' + (v.available ? '' : ', unavailable');
      if (v.size) {
        const next = [...pdp.querySelectorAll('[data-sv-set]')].find((set) => set.dataset.svSet === v.size);
        if (next) {
          showSet(next);
          setText('[data-sv-shown-size]', next.dataset.svShown || v.size); // a size without its own photo shows the closest one
        }
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());
    }

    // Size photos are stacked; the chosen one fades in over the previous one once it is decoded (no blank frame).
    const sets = [...pdp.querySelectorAll('[data-sv-set]')];
    sets.forEach((set) => { set.classList.toggle('is-active', !set.hidden); set.hidden = false; });
    let setToken = 0;
    async function showSet(next) {
      const cur = sets.find((s) => s.classList.contains('is-active'));
      if (next === cur) return;
      const token = ++setToken;
      const img = next.querySelector('img:not(.sv-stage__off)');
      if (img && !img.complete) {
        img.loading = 'eager';
        await Promise.race([new Promise((r) => { img.addEventListener('load', r, { once: true }); img.addEventListener('error', r, { once: true }); }), new Promise((r) => setTimeout(r, 2500))]);
      }
      if (img && img.decode) await img.decode().catch(() => {});
      if (token !== setToken) return;
      sets.forEach((s) => s.classList.remove('is-prev'));
      if (cur) { cur.classList.remove('is-active'); cur.classList.add('is-prev'); }
      next.classList.add('is-active');
      setTimeout(() => { if (cur && !cur.classList.contains('is-active')) cur.classList.remove('is-prev'); }, 1300);
    }
    // Load the other sizes' photos once the page is idle, so a size change is instant.
    idle(() => sets.forEach((s) => s.querySelectorAll('img[loading="lazy"]').forEach((im) => { im.loading = 'eager'; })), { timeout: 3000 });

    form.addEventListener('change', (e) => { if (e.target.closest('[data-sv-option]')) update(); });

    const showLight = (state) => setText('[data-sv-shown-light]', state === 'off' ? 'unlit' : 'lit');
    pdp.addEventListener('sv:light', (e) => showLight(e.detail.state));
    showLight(pdp.dataset.light);

    let busy = false;
    const buyLabel = pdp.querySelector('[data-sv-buy-label]');
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (busy) return;
      busy = true;
      errorBox.textContent = '';
      const label = buyLabel ? buyLabel.textContent : '';
      if (buyLabel) buyLabel.textContent = 'Adding to your bag\u2026';
      buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));
      try {
        const body = new FormData(form);
        [...body.keys()].filter((k) => k.startsWith('sv-option')).forEach((k) => body.delete(k));
        body.append('sections', 'mini-cart');
        const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST', body, headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.description || json.message || 'Add to bag rejected');
        const cart = await (await fetch(window.Shopify.routes.root + 'cart.js')).json();
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
        cart.sections = json.sections;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart, openMiniCart: true } }));
      } catch (err) {
        console.warn('Sorvola add to bag:', err);
        errorBox.textContent = 'This piece could not be added to your bag. Please try again.';
      } finally {
        busy = false;
        if (buyLabel) buyLabel.textContent = label;
        buttons.forEach((b) => b.removeAttribute('aria-busy'));
      }
    });

    const mainBuy = pdp.querySelector('[data-sv-main-buy]');
    const bar = document.querySelector('[data-sv-buybar]');
    if (mainBuy && bar && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        bar.classList.toggle('is-visible', !entry.isIntersecting);
      }).observe(mainBuy);
    }
  }

  // Phones: a horizontal swipe on the product photo turns the lights off (left) or on (right).
  function initSwipe(root) {
    root.querySelectorAll('[data-sv-swipe]').forEach((stage) => {
      let x0 = null, y0 = null;
      stage.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
      stage.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0, dy = e.changedTouches[0].clientY - y0;
        x0 = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
        const scope = stage.closest('[data-light-scope]');
        if (scope) setLight(scope, dx < 0 ? 'off' : 'on', true);
      }, { passive: true });
    });
  }

  function init(root) {
    initScopes(root);
    initSwipe(root);
    initHero(root);
    initProduct(root);
  }

  init(document);
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
